import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import {
  BaseSiteService,
  RoutingService
} from '@spartacus/core';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { RecaptchaService } from 'src/app/shared/services/recaptcha.service';
import { ForgotUserPasswordService } from '../forgot-user-password.service';

declare const grecaptcha: any;

declare global {
  interface Window {
    onRecaptchaLoad: () => void;
  }
}
@Component({
  selector: 'app-forgot-user-password',
  templateUrl: './forgot-user-password.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ForgotUserPasswordComponent implements OnInit, OnDestroy, AfterViewInit {
  passwordResetForm: FormGroup;
  recoverPasswordForm: FormGroup;
  recoverPasswordrequired = false;
  passwordResetRequired = false;
  emailRegex: any =
    '^([A-Za-z0-9\\._-]+)@([A-Za-z0-9\\._-]+)\\.([A-Za-z\\.]{2,8})(\\.[A-Za-z\\.]{2,8})?$';
  emailSubscription: any;
  isCaptchaEnabled$: Observable<boolean>;
  subRecaptcha: Subscription;
  recaptchaEnabled = false;
  siteKey: string | null = null;
  widgetId: any;

  constructor(
    private readonly fb: FormBuilder,
    private readonly forgotUserPasswordService: ForgotUserPasswordService,
    private readonly route: RoutingService,
    protected baseSiteService: BaseSiteService,
    private readonly recaptchaService: RecaptchaService
  ) {
    this.isCaptchaEnabled$ = this.baseSiteService
      .get()
      .pipe(map((baseSite: any) => baseSite?.captchaConfig?.enabled));
  }

  ngAfterViewInit(): void {
    if (window['grecaptcha'] && window['grecaptcha'].enterprise) {
      this.loadRecaptchas();
    }
  }

  ngOnInit() {
    this.passwordResetForm = this.fb.group({
      email: ['', [Validators.required, Validators.pattern(this.emailRegex)]],
    });
    this.recoverPasswordForm = this.fb.group({
      email: ['', [Validators.required, Validators.pattern(this.emailRegex)]],
    });
    this.subRecaptcha = this.recaptchaService
      .isEnabled$()
      .subscribe((enabled) => {
        this.recaptchaEnabled = enabled;
        this.siteKey = this.recaptchaService.getSiteKey();
        if (enabled) {
          this.passwordResetForm.addControl(
            'recaptcha',
            new FormControl('', Validators.required)
          );
          this.recoverPasswordForm.addControl(
            'recaptcha',
            new FormControl('', Validators.required)
          );
        }
      });
    window['onRecaptchaLoad'] = () => this.loadRecaptchas();
  }

  private loadRecaptchas() {
    this.loadRecaptcha1();
    this.loadRecaptcha2();
  }
  private loadRecaptcha1() {
    const container = document.getElementById('recaptcha-container-1');
    if (!container || !window['grecaptcha'] || !grecaptcha.enterprise.render) {
      console.error('Recaptcha not ready');
      return;
    }

    this.widgetId = grecaptcha.enterprise.render('recaptcha-container-1', {
      sitekey: this.siteKey,
      theme: 'light',
      size: 'normal',
      callback: (token: string) => this.onCaptchaSuccessPasswordForm(token)
    });
  }

  private loadRecaptcha2() {
    const container = document.getElementById('recaptcha-container-2');
    if (!container || !window['grecaptcha'] || !grecaptcha.enterprise.render) {
      console.error('Recaptcha not ready');
      return;
    }

    this.widgetId = grecaptcha.enterprise.render('recaptcha-container-2', {
      sitekey: this.siteKey,
      theme: 'light',
      size: 'normal',
      callback: (token: string) => this.onCaptchaSuccessResetForm(token)
    });
  }

  private onCaptchaSuccessResetForm(token: string) {
    //log('Captcha success token:', token);
    this.passwordResetForm.patchValue({ recaptcha: token });
  }

  private onCaptchaSuccessPasswordForm(token: string) {
    //log('Captcha success token:', token);
    this.recoverPasswordForm.patchValue({ recaptcha: token });
  }

  submitrecoverPasswordForm() {
    this.recoverPasswordForm.markAllAsTouched();
    if (this.recoverPasswordForm.invalid) {
      if (this.recoverPasswordForm.controls.email.errors?.required) {
        this.recoverPasswordrequired = true;
      }
      return false;
    }
    const params: any = {
      accountNumber: '',
      email: this.recoverPasswordForm.value.email,
      forgotPassword: false,
      forgotUsername: true,
      name: '',
      passwordAnswer: '',
      passwordQuestion: '',
      captchaToken: this.recoverPasswordForm.value.recaptcha
    };
    this.forgotUserPasswordService.validateEmailId(
      params,
      this.recoverPasswordForm.value.recaptcha
    );
    this.emailSubscription =
      this.forgotUserPasswordService.validateEmail$.subscribe((res) => {
        this.gotoQuestionPasswordPage(res);
      });
    return true;
  }

  ngOnDestroy() {
    if (this.emailSubscription) {
      this.emailSubscription.unsubscribe();
      this.subRecaptcha?.unsubscribe();
    }
  }

  validateRecoverPassword() {
    if (!this.recoverPasswordForm.controls.email.errors?.required) {
      this.recoverPasswordrequired = false;
    }
  }

  validatepasswordReset() {
    if (!this.passwordResetForm.controls.email.errors?.required) {
      this.passwordResetRequired = false;
    }
  }

  gotoQuestionPasswordPage(questionInfo: any) {
    if (questionInfo !== null) {
      if (questionInfo && questionInfo.passwordQuestion) {
        this.route.goByUrl('/login/userdatarecoveryaction');
      } else {
        this.route.goByUrl('/login/helphintquestionrecovery');
      }
    }
  }

  submitpasswordResetForm() {
    this.passwordResetForm.markAllAsTouched();
    if (this.passwordResetForm.invalid) {
      if (this.passwordResetForm.controls.email.errors?.required) {
        this.passwordResetRequired = true;
      }
      return false;
    }
    const params: any = {
      accountNumber: '',
      email: this.passwordResetForm.value.email,
      forgotPassword: true,
      forgotUsername: false,
      name: '',
      passwordAnswer: '',
      passwordQuestion: '',
      captchaToken: this.passwordResetForm.value.recaptcha
    };
    this.forgotUserPasswordService.validateEmailId(
      params,
      this.passwordResetForm.value.recaptcha
    );
    this.emailSubscription =
      this.forgotUserPasswordService.validateEmail$.subscribe((res) => {
        this.gotoQuestionPasswordPage(res);
      });
    return true;
  }
}
